import { ResultDigits } from "./ResultDigits";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check, Clock } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n";

const t12 = (tm) => { const [h, m] = tm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const fmtLeft = (s) => { const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60; return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${String(m).padStart(2, "0")}:${String(x).padStart(2, "0")}`; };

/* Today's full show timeline on the Result page: done shows with numbers, next show countdown, upcoming list. */
export const ShowTimeline = () => {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const ta = lang === "ta";
  const [d, setD] = useState(() => { try { return JSON.parse(localStorage.getItem("rl_timeline_cache") || "null"); } catch { return null; } });
  const [tick, setTick] = useState(0);
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const load = () => api.get("/lineup/today").then((r) => { setD(r.data); setTick(0); setStale(false); try { localStorage.setItem("rl_timeline_cache", JSON.stringify({ ...r.data, cached_at: Date.now() })); } catch {} }).catch(() => setStale(true));
    load();
    const p = setInterval(load, 20000);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(p); clearInterval(t); };
  }, []);
  if (!d?.shows?.length) return null;
  const left = d.next_seconds != null ? Math.max(0, d.next_seconds - tick) : null;
  const groups = Object.entries(d.shows.reduce((acc, s) => { (acc[s.draw_time] = acc[s.draw_time] || []).push(s); return acc; }, {}));
  return (
    <div className="rl-card p-3.5" data-testid="show-timeline">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-black tracking-wider text-slate-900 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-amber-600" />{ta ? "இன்றைய results" : "TODAY'S RESULTS"} <span className="text-slate-400 font-semibold">{d.done}/{d.total}</span></div>
        {stale && <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="timeline-stale">{ta ? "Offline · சேமித்தது" : "Offline · saved"}{d.cached_at ? ` ${new Date(d.cached_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}</span>}
        {!stale && d.next && left != null && (
          <button onClick={() => navigate(`/play/${d.next.game_id}`)} className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 flex items-center gap-1" data-testid="timeline-next">
            <Clock className="w-3 h-3" />{ta ? "அடுத்தது" : "Next"} {d.next.name} · {fmtLeft(left)}
          </button>
        )}
      </div>
      <div className="relative pl-5" data-testid="timeline-list">
        <div className="absolute left-[7px] top-1 bottom-1 w-0.5 bg-slate-200" />
        {groups.map(([tm, rows]) => {
          const isNext = d.next && rows.some((r) => r.game_id === d.next.game_id);
          const done = rows.every((r) => r.status === "done");
          return (
            <div key={tm} className="relative mb-2.5" data-testid={`timeline-slot-${tm.replace(":", "-")}`}>
              <span className={`absolute -left-5 top-1 w-4 h-4 rounded-full border-2 flex items-center justify-center ${done ? "bg-emerald-500 border-emerald-500" : isNext ? "bg-amber-400 border-amber-400 animate-pulse" : "bg-white border-slate-300"}`}>{done && <Check className="w-2.5 h-2.5 text-white" />}</span>
              <div className={`text-[10px] font-black tracking-widest mb-1 ${done ? "text-emerald-700" : isNext ? "text-amber-700" : "text-slate-400"}`}>{t12(tm)}{isNext && left != null ? ` · ${fmtLeft(left)}` : ""}</div>
              <div className="flex flex-wrap gap-1.5">
                {rows.map((s) => (
                  <button key={s.game_id} onClick={() => navigate(`/play/${s.game_id}`)} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-left ${s.status === "done" ? "bg-white border-slate-200" : s.game_id === d.next?.game_id ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid={`timeline-show-${s.game_id}`}>
                    <div><div className="text-[11px] font-bold text-slate-900 leading-tight">{s.name}</div><div className="text-[9px] text-slate-400">{s.category}</div></div>
                    {s.status === "done" ? <ResultDigits number={s.number} size="w-6 h-6 text-[11px]" /> : <span className="text-[10px] font-bold text-slate-500">{s.open ? (ta ? "OPEN" : "OPEN") : (ta ? "காத்திருங்கள்" : "waiting")}</span>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
